import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Pencil, Save, X, Plus, Trash2, ArrowUp, ArrowDown, Type, Image as ImageIcon } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import ZoomableImage from "@/components/ZoomableImage";

const ALIGN_CLASS = { left: "text-left items-start", center: "text-center items-center", right: "text-right items-end" };

export default function MemurBilgi() {
  const { user } = useAuth();
  const isSuperAdmin = user.role === "founder" || user.role === "admin";
  const [me, setMe] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [page, setPage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftBlocks, setDraftBlocks] = useState([]);
  const [draftTitle, setDraftTitle] = useState("");

  const canEdit = isSuperAdmin || me?.is_ps;

  useEffect(() => {
    api.get("/memur/me").then(r => setMe(r.data));
    api.get("/memur/bilgi").then(r => { setSections(r.data); if (r.data.length) setActiveKey(r.data[0].section_key); });
  }, []);

  const loadPage = (key) => api.get(`/memur/bilgi/${key}`).then(r => setPage(r.data));
  useEffect(() => { if (activeKey) loadPage(activeKey); }, [activeKey]);

  const startEdit = () => {
    setDraftBlocks(page.blocks.map(b => ({ ...b })));
    setDraftTitle(page.title);
    setEditing(true);
  };

  const addBlock = (type) => {
    setDraftBlocks([...draftBlocks, type === "text" ? { type: "text", text: "" } : { type: "image", url: "", align: "center" }]);
  };
  const removeBlock = (i) => setDraftBlocks(draftBlocks.filter((_, ii) => ii !== i));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draftBlocks.length) return;
    const copy = [...draftBlocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setDraftBlocks(copy);
  };
  const updateBlock = (i, patch) => setDraftBlocks(draftBlocks.map((b, ii) => ii === i ? { ...b, ...patch } : b));

  const save = async () => {
    try {
      await api.put(`/memur/bilgi/${activeKey}`, { title: draftTitle, blocks: draftBlocks });
      toast.success("Kaydedildi");
      setEditing(false);
      loadPage(activeKey);
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl">
      <header>
        <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// POLİS DEPARTMANI</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Memur Bilgi Sayfası</h1>
        <p className="text-zinc-500 mt-2">Yalnızca Polis Şefi (ve Yönetici/Kurucu) düzenleyebilir.</p>
      </header>

      <div className="grid md:grid-cols-[200px_1fr] gap-6">
        <div className="space-y-1">
          {sections.map(s => (
            <button key={s.section_key} onClick={() => { setActiveKey(s.section_key); setEditing(false); }}
              data-testid={`bilgi-nav-${s.section_key}`}
              className={`block w-full text-left px-3 py-2 text-sm border ${activeKey === s.section_key ? "bg-rose-950/30 border-rose-800 text-rose-300" : "bg-zinc-950 border-zinc-900 text-zinc-400 hover:bg-zinc-900"}`}>
              {s.title}
            </button>
          ))}
        </div>

        <Card className="bg-zinc-950 border-zinc-900 p-6 min-h-[300px]">
          {!page ? (
            <div className="text-zinc-500 text-sm">Yükleniyor...</div>
          ) : editing ? (
            <div className="space-y-4">
              <Input value={draftTitle} onChange={e => setDraftTitle(e.target.value)} data-testid="bilgi-title-input"
                className="bg-zinc-900 border-zinc-800 text-lg font-heading font-bold" />
              <div className="space-y-4">
                {draftBlocks.map((b, i) => (
                  <div key={i} className="border border-zinc-800 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono text-zinc-500">{b.type === "text" ? "METİN" : "RESİM"}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => moveBlock(i, -1)} data-testid={`block-up-${i}`}><ArrowUp className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => moveBlock(i, 1)} data-testid={`block-down-${i}`}><ArrowDown className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => removeBlock(i)} data-testid={`block-remove-${i}`} className="text-rose-400"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    {b.type === "text" ? (
                      <Textarea rows={4} value={b.text} onChange={e => updateBlock(i, { text: e.target.value })}
                        data-testid={`block-text-${i}`} className="bg-zinc-900 border-zinc-800" />
                    ) : (
                      <div className="space-y-2">
                        <ImageUploader value={b.url} onChange={(url) => updateBlock(i, { url })} multiple={false} label="Resim Seç" />
                        <div className="flex gap-2">
                          {["left", "center", "right"].map(a => (
                            <Button key={a} size="sm" type="button" variant={b.align === a ? "default" : "outline"}
                              onClick={() => updateBlock(i, { align: a })} data-testid={`block-align-${i}-${a}`}
                              className={b.align === a ? "bg-rose-600" : "border-zinc-700"}>{a === "left" ? "Sol" : a === "center" ? "Orta" : "Sağ"}</Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => addBlock("text")} data-testid="add-text-block" className="border-zinc-700"><Type className="w-4 h-4 mr-1" />Metin Ekle</Button>
                <Button size="sm" variant="outline" onClick={() => addBlock("image")} data-testid="add-image-block" className="border-zinc-700"><ImageIcon className="w-4 h-4 mr-1" />Resim Ekle</Button>
              </div>
              <div className="flex gap-2 pt-2 border-t border-zinc-900">
                <Button onClick={save} data-testid="bilgi-save-button" className="bg-rose-600 hover:bg-rose-700"><Save className="w-4 h-4 mr-2" />Kaydet</Button>
                <Button variant="outline" onClick={() => setEditing(false)} data-testid="bilgi-cancel-button" className="border-zinc-700"><X className="w-4 h-4 mr-2" />İptal</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold">{page.title}</h2>
                {canEdit && <Button size="sm" onClick={startEdit} data-testid="bilgi-edit-button" className="bg-rose-600 hover:bg-rose-700"><Pencil className="w-4 h-4 mr-2" />Düzenle</Button>}
              </div>
              {page.blocks.length === 0 && <p className="text-zinc-500 text-sm">Bu bölüm henüz doldurulmadı.</p>}
              {page.blocks.map((b, i) => (
                <div key={i} className={`flex flex-col ${ALIGN_CLASS[b.align] || ""}`}>
                  {b.type === "text" ? (
                    <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">{b.text}</p>
                  ) : (
                    <ZoomableImage src={b.url} className="max-w-full md:max-w-md border border-zinc-800" />
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
